export const CAPABILITIES = [
  {
    id: "01",
    slug: "digital-experiences",
    name: "Digital Experiences",
    summary:
      "Immersive websites, interactive campaigns, digital launches and experiential brand destinations.",
    seoTitle: "Digital Experiences — Immersive Websites & Launches | Elentara",
    seoDescription:
      "Elentara builds immersive websites, interactive campaigns, digital launches and experiential brand destinations — digital experiences beyond the expected.",
    build: [
      "Immersive brand destinations built around a single spatial idea",
      "Interactive campaign experiences with scroll-linked narrative",
      "Digital product and collection launches",
      "Experiential microsites for events, drops and openings",
    ],
    where: [
      "Flagship brand sites that need to feel like a place",
      "Campaign moments that need to be remembered, not scrolled past",
      "Launches where the experience is part of the product",
      "Digital destinations supporting a physical space",
    ],
    tech: ["Three.js / WebGL2", "React", "GLSL shaders", "Scroll-linked motion systems", "Headless content"],
    deliverables: [
      "Experience concept and interaction model",
      "Art direction and design system",
      "3D scenes and motion choreography",
      "Front-end engineering and performance tiering",
    ],
  },
  {
    id: "02",
    slug: "creative-technology",
    name: "Creative Technology",
    summary:
      "Generative systems, real-time experiences, experimental interfaces and technology-led concepts.",
    seoTitle: "Creative Technology — Generative & Real-Time Systems | Elentara",
    seoDescription:
      "Elentara builds generative systems, real-time experiences, experimental interfaces and technology-led concepts — creative technology where the idea leads.",
    build: [
      "Generative systems that produce rather than replay",
      "Real-time audio-, data- and sensor-reactive experiences",
      "Experimental interfaces that break the form-and-button pattern",
      "Technology-led concepts and proofs for pitch and R&D",
    ],
    where: [
      "Identity systems that need to be alive rather than static",
      "Performances, launches and installations driven by live input",
      "Products exploring a genuinely new interaction model",
      "Internal innovation programmes needing credible prototypes",
    ],
    tech: ["Custom GLSL", "Web Audio API", "GPU particle systems", "Live data pipelines", "Deterministic seeding"],
    deliverables: [
      "System architecture and mapping model",
      "Working real-time prototype",
      "Shader and simulation development",
      "Documentation for hand-off or performance use",
    ],
  },
  {
    id: "03",
    slug: "3d-design-motion",
    name: "3D Design & Motion",
    summary:
      "CGI, simulation, motion systems, product visualization and cinematic digital storytelling.",
    seoTitle: "3D Design & Motion — CGI, Simulation & Product Visualization | Elentara",
    seoDescription:
      "Elentara delivers CGI, simulation, motion systems, product visualization and cinematic digital storytelling — 3D design with the discipline of film.",
    build: [
      "Cinematic CGI films and product visualisation",
      "Fluid, particle and rigid-body simulation work",
      "Motion systems and choreography for interfaces and installations",
      "Real-time 3D scenes engineered for the browser",
    ],
    where: [
      "Product storytelling where the product cannot be filmed",
      "Launch films that need to live on the web, not only on video platforms",
      "Interfaces that need motion with hierarchy and intent",
      "Environments and objects that must exist before they physically exist",
    ],
    tech: ["Physically based rendering", "Simulation pipelines", "Real-time 3D", "Frame-sequence delivery", "Motion systems"],
    deliverables: [
      "Look development and lighting direction",
      "Simulation and animation passes",
      "Real-time or sequence-based delivery",
      "Motion specification for engineering",
    ],
  },
  {
    id: "04",
    slug: "innovation-labs",
    name: "Innovation Labs",
    summary:
      "WebAR, spatial interfaces, rapid prototypes, emerging technology and experimental R&D.",
    seoTitle: "Innovation Labs — WebAR, Spatial Interfaces & Prototypes | Elentara",
    seoDescription:
      "Elentara Innovation Labs: WebAR, spatial interfaces, rapid prototypes, emerging technology and experimental R&D — building what shouldn't exist yet.",
    build: [
      "Browser-based AR and spatial experiences",
      "Spatial interface prototypes for depth-first interaction",
      "Rapid prototypes that answer one question quickly",
      "Emerging-technology research with an honest verdict",
    ],
    where: [
      "Architecture and product decisions made on-site rather than on paper",
      "Innovation teams that need proof before commitment",
      "Retail and cultural spaces layering digital onto physical",
      "Early bets on interaction models that are not standard yet",
    ],
    tech: ["WebXR / WebAR", "Plane detection & world anchoring", "DeviceOrientation", "Rapid prototyping", "Mobile GPU budgeting"],
    deliverables: [
      "Prototype with a clearly scoped question",
      "Technical feasibility findings",
      "Device support and fallback matrix",
      "Recommendation on whether to proceed",
    ],
  },
  {
    id: "05",
    slug: "interactive-solutions",
    name: "Interactive Solutions",
    summary:
      "Interactive data experiences, custom digital tools, information systems and unconventional interfaces.",
    seoTitle: "Interactive Solutions — Data Experiences & Custom Tools | Elentara",
    seoDescription:
      "Elentara builds interactive data experiences, custom digital tools, information systems and unconventional interfaces — information with presence.",
    build: [
      "Interactive data experiences and data-art systems",
      "Custom digital tools built around a specific workflow",
      "Information systems designed for comprehension, not decoration",
      "Unconventional interfaces where the standard pattern fails",
    ],
    where: [
      "Editorial and media storytelling driven by real datasets",
      "Public installations that must be legible in seconds",
      "Configurators, calculators and decision tools",
      "Complex information that has outgrown a dashboard",
    ],
    tech: ["Streaming data ingest", "Instanced GPU rendering", "Sonification", "Custom visual encoding", "Accessible interaction models"],
    deliverables: [
      "Data model and encoding review",
      "Interaction and comprehension design",
      "Engineering and integration",
      "Accessibility and fallback strategy",
    ],
  },
];

export const getCapability = (slug) => CAPABILITIES.find((c) => c.slug === slug);
